import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { DomainRegistrationPlatform } from '../types/models/DomainRegistrationPlatform';
import type { ApiResponseDomainRegistrationPlatformList } from '../types/api/ApiResponseDomainRegistrationPlatform';

interface UseDomainRegistrationPlatformsReturn {
  platforms: DomainRegistrationPlatform[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useDomainRegistrationPlatforms = (): UseDomainRegistrationPlatformsReturn => {
  const [platforms, setPlatforms] = useState<DomainRegistrationPlatform[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchPlatforms = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiFetch('/datacenters/domain-names/domain-registration-platforms');

      if (!response.ok) {
        const body = (await response.json().catch(() => null)) as { message?: string } | null;
        throw new Error(body?.message ?? `HTTP error! status: ${response.status}`);
      }

      const data = (await response.json()) as ApiResponseDomainRegistrationPlatformList;
      if (data.status === 'success' && data.data?.domain_registration_platforms) {
        setPlatforms(data.data.domain_registration_platforms);
      } else if (data.status === 'fail' || data.status === 'error') {
        setError(data.message ?? 'Failed to load domain registration platforms');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching domain registration platforms');
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    if (auth.isAuthenticated) {
      fetchPlatforms();
    }
  }, [auth.isAuthenticated, fetchPlatforms]);

  return { platforms, loading, error, refetch: fetchPlatforms };
};
